"use client";

import { useNavigation } from "@app/[locale]/(site)/navContext";
import useTranslate from "@app/_hooks/useTranslate";
import type { ChangeEvent } from "react";
import { useMemo } from "react";

import { FEATURE_FLAGS } from "@/config/featureFlags";
import { env } from "@/env";
import getLeafCategories from "@/utils/navigation/getLeafCategories";

import Checkbox from "./checkbox";
import ExponentialRange from "./ExponentialRange";
import Range from "./Range";
import Wrapper from "./wrapper";

type FacetProps = {
  option: any;
  onChange: (event: ChangeEvent<HTMLInputElement>, attribute: string) => void;
  slug?: string;
};

type TranslateFn = ReturnType<typeof useTranslate>;

function getLabel(option: any, t: TranslateFn) {
  if (FEATURE_FLAGS.elevate) {
    let key = option.attribute ?? "";
    if (key === "custom.leafCategoryCodes") key = "category";
    if (key === "custom.statuses") key = "featured";
    if (key === "custom.designerName") key = "designer";
    if (key === "custom.norce_color_group") key = "norce_color_group";
    return t(key, { strict: true }) ?? option.name ?? "";
  }
  const key = option.name ?? "";
  return (
    (t(key.toLowerCase(), { strict: true }) || t(key, { strict: true })) ?? key
  );
}

const Facet = (props: FacetProps) => {
  const t = useTranslate();
  const { option, onChange } = props;

  if (
    option.type === "range" &&
    option.min !== undefined &&
    option.max !== undefined
  ) {
    const { min, max, values, attribute } = option;

    if (
      env.NEXT_PUBLIC_FEATURES_EXPONENTIAL_PRICE_FILTER === "true" &&
      attribute.toLowerCase() === "price"
    ) {
      return (
        <ExponentialRange
          {...option}
          min={min}
          max={max}
          values={values}
          attribute={attribute}
          suffix={option.extras?.suffix}
          minLabel={
            option.extras?.minLabel ? t(option.extras.minLabel) : undefined
          }
          maxLabel={
            option.extras?.maxLabel ? t(option.extras.maxLabel) : undefined
          }
          onChange={onChange}
        />
      );
    }

    return (
      <Range
        {...option}
        min={min}
        max={max}
        values={values}
        attribute={attribute}
        suffix={option.extras?.suffix}
        minLabel={
          option.extras?.minLabel ? t(option.extras.minLabel) : undefined
        }
        maxLabel={
          option.extras?.maxLabel ? t(option.extras.maxLabel) : undefined
        }
        onChange={onChange}
      />
    );
  }

  if (!option.items?.length) {
    return null;
  }

  if (option.type === "checkbox") {
    return <Checkbox {...option} onChange={onChange} />;
  }

  return null;
};

type Props = {
  filter: any[];
  onChange: any;
  slug?: string;
};
export default function Filter(props: Props) {
  const t = useTranslate();
  const { filter, onChange, slug = "root" } = props;
  const { flatNavigation, commerceSlugPairs } = useNavigation().state;
  const handleChange = (
    event: ChangeEvent<HTMLInputElement>,
    attribute: string,
  ) => {
    const { target } = event;
    const localFilters = [...filter];
    const index = localFilters.findIndex(
      (facet) => facet.attribute === attribute,
    );
    const localFilter = { ...localFilters[index] };

    if (localFilter.type === "range") {
      const { min, max } = target;
      localFilter.values = [Number(min), Number(max)];
    } else if (target.checked && localFilter.type === "checkbox") {
      localFilter.values = [...(localFilter.values ?? []), target.name];
    } else if (localFilter.values?.length) {
      localFilter.values = localFilter.values.filter(
        (value: string) => value !== target.name,
      );
    }

    localFilters.splice(index, 1, localFilter);

    onChange(localFilters);
  };

  const displayedFilters = useMemo<any[]>(
    () =>
      [...filter].map((f) => {
        if (FEATURE_FLAGS.elevate) {
          return f;
        }
        if (f.name !== "Category" || !f.items) return f;
        const currentCategory = flatNavigation?.[slug]?.children?.length
          ? flatNavigation?.[slug]
          : flatNavigation?.root;

        if (!currentCategory?.children?.length) {
          return {
            ...f,
            items: [],
          };
        }

        const actualCategories = getLeafCategories(
          currentCategory,
          flatNavigation as any,
        );

        const actualCategorySet = new Set(actualCategories);
        const items = f.items.filter((item: any) => {
          const _slug = commerceSlugPairs?.[item.name];

          return item.count > 0 && actualCategorySet.has(_slug as string);
        });

        return {
          ...f,
          items,
        };
      }),
    [slug, filter, flatNavigation, commerceSlugPairs],
  );

  const visibleFilterCount = displayedFilters.filter(
    (option: any) => option.show && (!option.items || option.items?.length > 1),
  ).length;

  return (
    <div className="border-gray-150 hr-y last:child:border-b grid w-full grid-flow-row overflow-hidden px-3">
      {displayedFilters.map((option: any) => {
        let shouldOpen =
          option.name === "Category" ||
          option.name === "featured" ||
          visibleFilterCount <= 3;

        if (FEATURE_FLAGS.elevate) {
          shouldOpen =
            option.attribute === "custom.leafCategoryCodes" ||
            option.attribute === "custom.statuses";
        }

        if (option.name !== "featured" && option.items?.length === 1) {
          return null;
        }

        return (
          option.show && (
            <Wrapper
              open={shouldOpen}
              key={option.attribute}
              label={getLabel(option, t)}
              slug={slug}
              totalSelected={
                option.type === "checkbox" && option?.values?.length
              }
            >
              <Facet option={option} slug={slug} onChange={handleChange} />
            </Wrapper>
          )
        );
      })}
    </div>
  );
}
